import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import GUI from 'lil-gui'
import options from "./js/options.js";
import light from "./js/light.js";
import baseFloor from "./js/base-floor.js";
import loadingManager from "./js/loader.js";
import {pdfCreate} from "./js/pdf.js";
import {ARButton, EXRLoader} from "three/addons";
import theme from "./js/theme.js";
import {initAIImageGenerator} from "./js/ai.js";

const canvasContainer = document.querySelector('#canvas-container');
const canvasContainerSize = canvasContainer.getBoundingClientRect()
const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()
const isLarge = canvasContainer.getBoundingClientRect().width > 600

const dracoLoader = new DRACOLoader(loadingManager)
dracoLoader.setDecoderPath('./draco/')
const gltfLoader = new GLTFLoader(loadingManager)
gltfLoader.setDRACOLoader(dracoLoader)

const sizes = {
    width: canvasContainerSize.width,
    height: canvasContainerSize.height
}

const lights = light(scene)
baseFloor(THREE, scene)

const camera = new THREE.PerspectiveCamera(45, sizes.width / sizes.height, 0.1, 100)
isLarge ? camera.position.set(4.97, 0.87, 1.88) : camera.position.set(5.43, 0.35, 2.63)
scene.add(camera)

const controls = new OrbitControls(camera, canvas)
controls.target.set(0, 0, 0)
controls.enableDamping = true
controls.enablePan = false
controls.minDistance = 2
controls.maxDistance = 9
controls.maxPolarAngle = Math.PI / 2
controls.minAzimuthAngle = 0
controls.maxAzimuthAngle = Math.PI

window.addEventListener('resize', () => {
    sizes.width = canvasContainer.clientWidth
    sizes.height = canvasContainer.clientHeight
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 3))
    backgroundMaterial.uniforms.uAspect.value = sizes.width / sizes.height
})

const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
    preserveDrawingBuffer: true,
    alpha: true
})
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap
renderer.setClearColor('#ffffff')
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 0.64
renderer.outputColorSpace = THREE.SRGBColorSpace

const pmremGenerator = new THREE.PMREMGenerator(renderer)
pmremGenerator.compileEquirectangularShader()

new EXRLoader().load('./models/wide_street_01_1k.exr', function (texture) {
    texture.mapping = THREE.EquirectangularReflectionMapping
    scene.environment = texture
    scene.environmentIntensity = 0.36
})

const backgroundMaterial = new THREE.ShaderMaterial({
    depthTest: false,
    depthWrite: false,
    uniforms: {
        centerColor: { value: new THREE.Color(0x999999) },
        edgeColor:   { value: new THREE.Color(0x999999) },
        uAspect:     { value: sizes.width / sizes.height }
    },
    vertexShader: `
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = vec4(position.xy, 1.0, 1.0);
        }`,
    fragmentShader: `
        varying vec2 vUv;
        uniform vec3 centerColor;
        uniform vec3 edgeColor;
        uniform float uAspect;
        float random(vec2 coords) {
            return fract(sin(dot(coords.xy, vec2(12.9898, 78.233))) * 43758.5453);
        }
        void main() {
            vec2 uv = vUv;
            uv.x = (vUv.x - 0.5) * uAspect + 0.5;
            float factor = smoothstep(0.0, 1.2, distance(uv, vec2(0.5)));
            vec3 finalColor = mix(centerColor, edgeColor, factor);
            float noise = (random(vUv) - 0.5) * (1.0 / 255.0);
            gl_FragColor = vec4(finalColor + noise, 1.0);
        }`
})
const backgroundMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), backgroundMaterial)
backgroundMesh.frustumCulled = false
backgroundMesh.renderOrder = -1
scene.add(backgroundMesh)

theme(scene, backgroundMaterial)

const gui = new GUI({ title: 'Настройки фона' })
gui.hide()
const bgFolder = gui.addFolder('Фон')
const bgState = {
    centerColor: '#' + backgroundMaterial.uniforms.centerColor.value.getHexString(),
    edgeColor:   '#' + backgroundMaterial.uniforms.edgeColor.value.getHexString(),
}
bgFolder.addColor(bgState, 'centerColor').name('Центр').onChange(v => {
    backgroundMaterial.uniforms.centerColor.value.set(v)
})
bgFolder.addColor(bgState, 'edgeColor').name('Края').onChange(v => {
    backgroundMaterial.uniforms.edgeColor.value.set(v)
})
bgFolder.open()

const modelFolder = gui.addFolder('Модель')
const modelState = { positionY: 0 }
modelFolder.add(modelState, 'positionY', -3, 3, 0.01).name('Позиция Y').onChange(v => {
    if (state.currentModel) state.currentModel.position.y = v
})
modelFolder.open()

const clock = new THREE.Clock()
let previousTime = 0

const state = { currentModel: null }
const modelsLibrary = {}

// ── Загружаем только модель 2 ────────────────────────────────────────
gltfLoader.load('./models/2.glb', async (gltf) => {
    modelsLibrary['2'] = gltf.scene

    state.currentModel = modelsLibrary['2']
    state.currentModel.traverse((child) => {
        if (child.isMesh) {
            child.castShadow = true
            child.receiveShadow = true
        }
    })

    scene.add(state.currentModel)

    options(state, modelsLibrary, scene, camera, renderer, lights)
    await pdfCreate(renderer, camera, scene, state, backgroundMaterial)
})

initAIImageGenerator({ renderer, camera, scene })

renderer.xr.enabled = true

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream

if (isIOS) {
    const iosButton = document.createElement('button')
    iosButton.id = 'ARButtonIOS'
    iosButton.onclick = () => {
        const anchor = document.createElement('a')
        anchor.setAttribute('rel', 'ar')
        anchor.appendChild(document.createElement('img'))
        anchor.setAttribute('href', './models/2.usdz')
        anchor.click()
    }
    canvasContainer.appendChild(iosButton)
} else {
    const webXRButton = ARButton.createButton(renderer, { requiredFeatures: ['hit-test'] })
    canvasContainer.appendChild(webXRButton)
}

const reticle = new THREE.Mesh(
    new THREE.RingGeometry(0.1, 0.12, 32).rotateX(-Math.PI / 2),
    new THREE.MeshBasicMaterial({ color: 0x00ff00 })
)
reticle.matrixAutoUpdate = false
reticle.visible = false
scene.add(reticle)

let hitTestSource = null
let hitTestSourceRequested = false

renderer.setAnimationLoop((time, frame) => {
    if (frame) {
        const session = renderer.xr.getSession()

        if (!hitTestSourceRequested) {
            session.requestReferenceSpace('viewer').then((referenceSpace) => {
                session.requestHitTestSource({ space: referenceSpace }).then((source) => {
                    hitTestSource = source
                })
            })
            hitTestSourceRequested = true
        }

        if (hitTestSource) {
            const hitTestResults = frame.getHitTestResults(hitTestSource)
            if (hitTestResults.length) {
                const hit = hitTestResults[0]
                const referenceSpace = renderer.xr.getReferenceSpace()
                const pose = hit.getPose(referenceSpace)

                reticle.visible = true
                reticle.matrix.fromArray(pose.transform.matrix)

                if (state.currentModel) {
                    state.currentModel.visible = true
                    state.currentModel.position.setFromMatrixPosition(reticle.matrix)
                    const lookAtTarget = new THREE.Vector3(camera.position.x, state.currentModel.position.y, camera.position.z)
                    state.currentModel.lookAt(lookAtTarget)
                }
            } else {
                reticle.visible = false
            }
        }
    }

    renderer.render(scene, camera)
})

renderer.xr.addEventListener('sessionstart', () => {
    controls.enabled = false
    if (state.currentModel) {
        state.currentModel.position.z = -3
        state.currentModel.position.y = -1
    }
})

renderer.xr.addEventListener('sessionend', () => {
    if (state.currentModel) {
        state.currentModel.position.set(0, 0.3, 0)
        state.currentModel.rotation.y = Math.PI / 2 + 0.6
        state.currentModel.scale.set(1, 1, 1)
        state.currentModel.visible = true
    }
    controls.enabled = true
    camera.position.set(9, 3, 4)
    controls.update()
    if (reticle) reticle.visible = false
    hitTestSourceRequested = false
    hitTestSource = null
})

canvas.addEventListener('click', (e) => {
    if (e.shiftKey) {
        const p = camera.position
        console.log(`camera.position.set(${p.x.toFixed(2)}, ${p.y.toFixed(2)}, ${p.z.toFixed(2)})`)
    }
})

const tick = () => {
    const elapsedTime = clock.getElapsedTime()
    const deltaTime = elapsedTime - previousTime
    previousTime = elapsedTime

    controls.update()
    renderer.render(scene, camera)
    window.requestAnimationFrame(tick)
}

tick()